import {createGlobalThemeContract, styleVariants} from "@vanilla-extract/css";
import {cardStateColors as col, dsColors, oneOffPalettes} from "../../css/tokens";
import {applyThemeVars, createThemeBuilder, toNamedContract} from "../../css/themes/theme-utils";
import {colorThemeVars, lightTheme} from "../../css/themes/themes.css";

const create400based = (prefix: string, {darkIcon}: {darkIcon: boolean} = {darkIcon: false}) => {
  const statusColor = col as Record<string, string>;
  return {
    main: statusColor[`${prefix}400`],
    mainBorder: statusColor[`${prefix}300`],
    onMainPrimary: dsColors.white,
    onMainSecondary: statusColor[`${prefix}50`],
    onMainDecoration: statusColor[darkIcon ? `${prefix}600` : `${prefix}200`],

    trackBg: statusColor[`${prefix}200`],
    trackBar: statusColor[`${prefix}600`],

    light: statusColor[`${prefix}25`],
    lightHover: statusColor[`${prefix}50`],
    onLightPrimary: statusColor[`${prefix}500`],

    buttonBg: statusColor[`${prefix}500`],
    buttonBgHover: statusColor[`${prefix}600`],
    buttonBorder: statusColor[`${prefix}200`],
  };
};

const themeShape = {
  unassigned: {
    main: [dsColors.gray150, dsColors.gray300],
    mainBorder: [dsColors.gray200, dsColors.gray350],
    onMainPrimary: [dsColors.gray400, dsColors.gray750],
    onMainSecondary: [dsColors.gray300, dsColors.gray650],
    onMainDecoration: [dsColors.gray350, dsColors.gray550],

    trackBg: [dsColors.gray100, dsColors.gray250],
    trackBar: [dsColors.gray250, dsColors.gray500],

    light: [dsColors.gray100, dsColors.gray250],
    lightHover: [dsColors.gray150, dsColors.gray300],
    onLightPrimary: [dsColors.gray500, dsColors.gray650],

    buttonBg: [dsColors.gray250, dsColors.gray400],
    buttonBgHover: [dsColors.gray300, dsColors.gray450],
    buttonBorder: [dsColors.gray100, dsColors.gray250],
  },
  assigned: create400based("assigned", {darkIcon: true}),
  started: create400based("started"),
  snoozing: create400based("snoozing"),
  blocked: create400based("blocked"),
  review: create400based("review"),
  done: create400based("done"),
  deleted: {
    main: dsColors.gray750,
    mainBorder: dsColors.gray500,
    onMainPrimary: dsColors.gray200,
    onMainSecondary: dsColors.gray300,
    onMainDecoration: dsColors.gray400,

    trackBg: dsColors.gray300,
    trackBar: dsColors.gray550,

    light: dsColors.gray50,
    lightHover: dsColors.gray100,
    onLightPrimary: dsColors.gray350,

    buttonBg: dsColors.gray600,
    buttonBgHover: dsColors.gray500,
    buttonBorder: dsColors.gray400,
  },
  archived: {
    main: dsColors.gray550,
    mainBorder: dsColors.gray450,
    onMainPrimary: dsColors.gray150,
    onMainSecondary: dsColors.gray250,
    onMainDecoration: dsColors.gray350,

    trackBg: dsColors.gray250,
    trackBar: dsColors.gray400,

    light: dsColors.dimPurple100,
    lightHover: dsColors.dimPurple150,
    onLightPrimary: dsColors.dimPurple350,

    buttonBg: dsColors.gray500,
    buttonBgHover: dsColors.gray600,
    buttonBorder: dsColors.gray300,
  },
  workflowItem: {
    main: dsColors.purple650,
    mainBorder: dsColors.purple550,
    onMainPrimary: dsColors.white,
    onMainSecondary: dsColors.purple200,
    onMainDecoration: dsColors.purple400,

    trackBg: dsColors.purple250,
    trackBar: dsColors.purple400,

    light: dsColors.purple100,
    lightHover: dsColors.purple150,
    onLightPrimary: dsColors.purple500,

    buttonBg: dsColors.purple500,
    buttonBgHover: dsColors.purple600,
    buttonBorder: dsColors.purple300,
  },
  hero: {
    main: oneOffPalettes.gold600,
    mainBorder: oneOffPalettes.gold500,
    onMainPrimary: dsColors.white,
    onMainSecondary: oneOffPalettes.gold200,
    onMainDecoration: oneOffPalettes.gold300,

    trackBg: oneOffPalettes.gold200,
    trackBar: oneOffPalettes.gold700,

    light: oneOffPalettes.gold200,
    lightHover: oneOffPalettes.gold300,
    onLightPrimary: oneOffPalettes.gold700,

    buttonBg: oneOffPalettes.gold500,
    buttonBgHover: oneOffPalettes.gold600,
    buttonBorder: oneOffPalettes.gold200,
  },
  doc: {
    main: [col.doc50, col.doc100],
    mainBorder: [col.doc100, col.doc200],
    onMainPrimary: [col.doc400, col.doc500],
    onMainSecondary: [col.doc300, col.doc400],
    onMainDecoration: [col.doc400, col.doc500],

    trackBg: [col.doc200, col.doc300],
    trackBar: [col.doc400, col.doc500],

    light: [col.doc25, col.doc25],
    lightHover: [col.doc50, col.doc50],
    onLightPrimary: [col.doc400, col.doc400],

    buttonBg: [col.doc25, col.doc50],
    buttonBgHover: [col.doc100, col.doc100],
    buttonBorder: [col.doc200, col.doc200],
  },
} as const;

export const cardStatusSetThemeVars = createGlobalThemeContract(
  toNamedContract(themeShape, "cdx-status-")
);
export const cardStatusThemeVars = createGlobalThemeContract(
  toNamedContract(themeShape.assigned, "cdx-status-")
);
const buildTheme = createThemeBuilder(cardStatusSetThemeVars);
const rootTheme = buildTheme(null, themeShape, {name: "card-status-colors-default", asRoot: true});

const alternateDone = {
  main: col.doneColorblind100,
  mainBorder: col.doneColorblind200,
  onMainPrimary: col.doneColorblind500,
  onMainSecondary: col.doneColorblind400,
  onMainDecoration: col.doneColorblind500,

  trackBg: col.doneColorblind25,
  trackBar: col.doneColorblind500,

  light: col.doneColorblind25,
  lightHover: col.doneColorblind50,
  onLightPrimary: col.doneColorblind400,

  buttonBg: col.doneColorblind25,
  buttonBgHover: col.doneColorblind100,
  buttonBorder: col.doneColorblind200,
} as const;

export const rootStatusColorOverwrites = {
  default: rootTheme,
  protanopia: buildTheme(
    rootTheme,
    {
      snoozing: {
        main: col.snoozingColorblind200,
        mainBorder: col.snoozingColorblind300,
        onMainPrimary: dsColors.white,
        onMainSecondary: col.snoozingColorblind25,
        onMainDecoration: col.snoozingColorblind500,

        trackBg: col.snoozingColorblind50,
        trackBar: col.snoozingColorblind500,

        light: col.snoozingColorblind25,
        lightHover: col.snoozingColorblind50,
        onLightPrimary: col.snoozingColorblind400,

        buttonBg: col.snoozingColorblind400,
        buttonBgHover: col.snoozingColorblind500,
        buttonBorder: col.snoozingColorblind100,
      },
      done: alternateDone,
    },
    {name: "card-status-colors-protanopia"}
  ),
  protanomaly: buildTheme(
    rootTheme,
    {
      snoozing: {
        main: col.snoozing100,
        mainBorder: col.snoozing200,
        onMainPrimary: dsColors.white,
        onMainSecondary: col.snoozing25,
        onMainDecoration: col.snoozing300,

        trackBg: col.snoozing25,
        trackBar: col.snoozing400,

        light: col.snoozing25,
        lightHover: col.snoozing50,
        onLightPrimary: col.snoozing300,

        buttonBg: col.snoozing200,
        buttonBgHover: col.snoozing300,
        buttonBorder: col.snoozing50,
      },
    },
    {name: "card-status-colors-protanomaly"}
  ),
  deuteranopia: buildTheme(
    rootTheme,
    {
      done: alternateDone,
      doc: {
        main: col.docColorblind25,
        mainBorder: col.docColorblind50,
        onMainPrimary: col.docColorblind400,
        onMainSecondary: col.docColorblind300,
        onMainDecoration: col.docColorblind300,

        trackBg: col.docColorblind100,
        trackBar: col.docColorblind300,

        light: col.docColorblind25,
        lightHover: col.docColorblind50,
        onLightPrimary: col.docColorblind400,

        buttonBg: col.docColorblind25,
        buttonBgHover: col.docColorblind100,
        buttonBorder: col.docColorblind200,
      },
    },
    {name: "card-status-colors-deuteranopia"}
  ),
};

export const cardStatusVariants = styleVariants(cardStatusSetThemeVars, (val) =>
  applyThemeVars(cardStatusThemeVars, val)
);

const buildContainerTheme = createThemeBuilder(colorThemeVars);

export const cardStatusBaseTheme = buildContainerTheme(
  lightTheme,
  {
    bg: cardStatusThemeVars.main,
    text: {
      primary: cardStatusThemeVars.onMainPrimary,
      secondary: cardStatusThemeVars.onMainSecondary,
    },
    border: {default: cardStatusThemeVars.mainBorder},
    elementTheme: {
      default: {
        contrast: {onTransparent: cardStatusThemeVars.onMainPrimary},
      },
    },
  },
  {name: "theme-cardStatusBase"}
);
